import { hierarchy, HierarchyPointNode, tree } from 'd3-hierarchy';
import { FlowEdge, FlowNode, Rank, TreeNodes } from './networking.types';
import { graphlib, layout } from 'dagre';
import { Node } from 'reactflow';
import { RankNumber, rankMapping, athleteRanges } from './networking.constants';
import { NodeData } from './networking.types';

const g = tree<TreeNodes>();

const nodeWidth = 300;
const nodeHeight = 275;

export function addPlaceholderChildren(node: TreeNodes, organization: string) {
    if (node.is_placeholder) {
        return;
    }
    // Initialize children array if it doesn't exist
    if (!node.children) {
        node.children = [];
    }
    for (let i = 0; i < 2; i++) {
        if (node.children.length < 2) {
            node.children.push({
                name: `${node.partner}-placeholder-${i}`,
                partner: `create-${node.partner}-${i}`, // Create unique partner ID for placeholder
                partner_details: {
                    name: `Create`,
                    leg: i === 0 ? 'L' : 'R', // Explicitly set leg for placeholder nodes
                },
                leadership_rank: 0,
                levels: {
                    athleteCount: 0,
                    fiyrpodAthleteCount: 0,
                    organizationSubscriptionLevel: 0,
                    marketplaceSales: 0,
                    marketplaceSalesTotal: 0,
                },
                organization,
                rank: 0,
                is_placeholder: true,
                children: [],
            });
        }
    }
    if (node.children && Array.isArray(node.children)) {
        node.children.forEach((n) => addPlaceholderChildren(n, organization));
    }
    return node;
}

export const getLayoutedElements = (
    rootNode: TreeNodes,
    organization: string,
    addPlaceholders: boolean = true,
    ranks: Rank[] = [],
    orientation: 'vertical' | 'horizontal' = 'vertical',
): { nodes: FlowNode[]; edges: FlowEdge[] } => {
    if (addPlaceholders) {
        addPlaceholderChildren(rootNode, organization);
    }

    if (orientation === 'vertical') {
        // Use d3-hierarchy for vertical layout
        const d3Hierarchy = hierarchy(rootNode);
        const treeLayout = g.nodeSize([nodeWidth, nodeHeight]);
        const layout: HierarchyPointNode<TreeNodes> = treeLayout(d3Hierarchy);
        const descendants = layout.descendants();

        // Create nodes with partner as ID
        const defaultLevels = {
            marketplaceSales: 0,
            organizationSubscriptionLevel: 0,
            athleteCount: 0,
            fiyrpodAthleteCount: 0,
        };
        const flowNodes = descendants.map((node: HierarchyPointNode<any>) => ({
            ...node.data,
            id: node.data.partner, // Changed from node.data.name to node.data.partner
            data: {
                partnerId: node.data.partner, // Changed from node.data.name
                label: node.data.partner_details?.name || '',
                parent: node.parent ? node.parent.data.partner : '', // Changed from node.parent.data.name
                parentId: node.parent ? node.parent.data.partner : '', // Changed from node.parent.data.partner
                matchesFilter: true,
                rank: node.data.rank || 0,
                leadership_rank: node.data.leadership_rank || 0,
                levels:
                    node.data.levels && typeof node.data.levels === 'object'
                        ? { ...defaultLevels, ...node.data.levels }
                        : defaultLevels,
                treeNode: node.data,
                context: {
                    organization: node.data.organization,
                    ranks: ranks,
                },
            },
            type: node.data.is_placeholder ? 'inviteNewPartner' : 'partnerViewer',
            position: { x: node.x, y: node.y },
            draggable: false,
        }));

        // Create edges using globally unique IDs
        const edges = descendants
            .filter((node) => node.parent)
            .map((node, index) => ({
                id: `e${node.parent!.data.partner}-${node.data.partner}-${index}`, // Added index for uniqueness
                source: node.parent!.data.partner,
                target: node.data.partner,
                type: 'smoothstep',
                sourceHandle: node.data.partner_details?.leg?.toLowerCase() || 'l',
            }));

        return { nodes: flowNodes, edges };
    } else {
        // Use Dagre for horizontal layout
        const graph = new graphlib.Graph();
        graph.setGraph({ rankdir: 'LR', ranksep: nodeHeight, nodesep: nodeWidth / 2 });

        const addNodeRecursively = (node: TreeNodes, parent: string | null = null) => {
            if (!node || !node.partner) return; // Changed from !node.name

            graph.setNode(node.partner, { width: nodeWidth, height: nodeHeight }); // Changed from node.name
            if (parent) {
                graph.setEdge(parent, node.partner, { weight: 1 }); // Changed from node.name
            }
            if (node.children && Array.isArray(node.children)) {
                node.children.forEach((child) => addNodeRecursively(child, node.partner)); // Changed from node.name
            }
        };

        addNodeRecursively(rootNode);

        try {
            layout(graph);
        } catch (error) {
            console.error('Error in Dagre layout:', error);
            return getLayoutedElements(rootNode, organization, false, ranks, 'vertical');
        }

        const defaultLevels = {
            marketplaceSales: 0,
            organizationSubscriptionLevel: 0,
            athleteCount: 0,
            fiyrpodAthleteCount: 0,
        };
        const flowNodes: FlowNode[] = [];
        graph.nodes().forEach((nodeId) => {
            const node = graph.node(nodeId);
            const originalNode = findNodeByPartner(rootNode, nodeId); // Changed from findNodeByName
            if (originalNode && node) {
                flowNodes.push({
                    ...originalNode,
                    id: nodeId,
                    data: {
                        partnerId: originalNode.partner,
                        label: originalNode.partner_details?.name || '',
                        parent: graph.predecessors(nodeId)?.[0] || '',
                        rank: originalNode.rank || 0,
                        matchesFilter: true,
                        leadership_rank: originalNode.leadership_rank || 0,
                        levels:
                            originalNode.levels && typeof originalNode.levels === 'object'
                                ? { ...defaultLevels, ...originalNode.levels }
                                : defaultLevels,
                        treeNode: originalNode,
                        context: {
                            organization: organization,
                            ranks: ranks,
                        },
                    },
                    type: originalNode.is_placeholder ? 'inviteNewPartner' : 'partnerViewer',
                    position: { x: node.x, y: node.y },
                    draggable: false,
                });
            }
        });

        const edges: FlowEdge[] = graph.edges().map((e, index) => ({
            id: `e${e.v}-${e.w}-${index}`, // Added index for uniqueness
            source: e.v,
            target: e.w,
            type: 'smoothstep',
            sourceHandle:
                findNodeByPartner(rootNode, e.w)?.partner_details?.leg?.toLowerCase() || 'l',
        }));

        return { nodes: flowNodes, edges };
    }
};

// New helper function to replace findNodeByName
const findNodeByPartner = (node: TreeNodes, partnerId: string): TreeNodes | null => {
    if (node.partner === partnerId) return node;
    if (node.children) {
        for (const child of node.children) {
            const found = findNodeByPartner(child, partnerId);
            if (found) return found;
        }
    }
    return null;
};

// Removed original findNodeByName function since it's no longer needed

export const createEdgesFromNodes = (nodes: FlowNode[]): FlowEdge[] => {
    const parentChildMap = new Map<string, string[]>();

    nodes.forEach((node) => {
        if (node.data.parent) {
            if (!parentChildMap.has(node.data.parent)) {
                parentChildMap.set(node.data.parent, []);
            }
            parentChildMap.get(node.data.parent)?.push(node.id);
        }
    });

    return nodes
        .filter((node) => Boolean(node.data.parent))
        .map((node, index) => {
            const position = node.data.treeNode?.partner_details?.leg?.toLowerCase() || 'l';

            return {
                id: `e${node.data.parent}-${node.id}-${index}`, // Added index for uniqueness
                source: node.data.parent,
                target: node.id,
                type: 'smoothstep',
                sourceHandle: position,
            };
        });
};

export const convertRankLevelToRank = (rankLevel: number, ranks: Rank[]): string => {
    return ranks.find((rank) => rank.level === rankLevel)?.name || 'Not Ranked';
};

export const convertOrganizationLevelToLevel = (organizationLevel: number): string => {
    switch (organizationLevel) {
        case 1:
            return 'Bronze';
        case 2:
            return 'Silver';
        case 3:
            return 'Gold';
        default:
            return '-';
    }
};

export const isRankNumber = (rank: number): rank is RankNumber => {
    return rank >= 0 && rank <= 5;
};

export const filterNodes = (
    nodes: Node[],
    nameFilter: string,
    rankFilter: string[],
    salesFilter: number | '',
    subscriptionFilter: string[],
    athleteFilter: string[],
) => {
    // Define default levels once at the top
    const defaultLevels = {
        marketplaceSales: 0,
        organizationSubscriptionLevel: 0,
        athleteCount: 0,
        fiyrpodAthleteCount: 0,
    };

    return nodes.map((node) => {
        // Early return for invalid nodes
        if (!node) {
            return {
                id: '',
                position: { x: 0, y: 0 },
                data: {
                    label: '',
                    rank: 0,
                    levels: defaultLevels,
                    matchesFilter: false,
                },
            };
        }

        // Ensure data exists
        const nodeData = node.data || {};

        // Create a safe data object with guaranteed structure
        const data: NodeData = {
            label: nodeData.label || '',
            rank: nodeData.rank || 0,
            levels: defaultLevels, // Start with defaults
            ...nodeData,
        };

        // CRITICAL: Always ensure levels is a valid object with all properties
        if (nodeData.levels && typeof nodeData.levels === 'object' && nodeData.levels !== null) {
            // Merge existing levels with defaults to ensure all properties exist
            data.levels = {
                ...defaultLevels,
                ...nodeData.levels,
            };
        } else {
            // Use defaults if levels is missing or invalid
            data.levels = { ...defaultLevels };
        }

        // Now we can safely access data.levels properties
        // Double-check that levels is valid (defensive programming)
        const levels =
            data.levels && typeof data.levels === 'object' && data.levels !== null
                ? { ...defaultLevels, ...data.levels }
                : defaultLevels;

        const nameMatch = (data.label || '').toLowerCase().includes(nameFilter.toLowerCase());

        let rankMatch = rankFilter.length === 0;
        if (!rankMatch && isRankNumber(data.rank)) {
            rankMatch = rankFilter.includes(rankMapping[data.rank]);
        } else if (!rankMatch) {
            rankMatch = rankFilter.includes('Not Ranked');
        }

        // Safe access - levels is guaranteed to exist and have all properties
        const marketplaceSales = levels.marketplaceSales || 0;
        const salesMatch = salesFilter === '' || marketplaceSales >= salesFilter;

        const organizationSubscriptionLevel = levels.organizationSubscriptionLevel || 0;
        const subscriptionLevel =
            ['Bronze', 'Silver', 'Gold'][organizationSubscriptionLevel - 1] || '-';
        const subscriptionMatch =
            subscriptionFilter.length === 0 || subscriptionFilter.includes(subscriptionLevel);

        const athleteCount = levels.athleteCount || 0;
        const athleteMatch =
            athleteFilter.length === 0 ||
            athleteFilter.some((range) => {
                const { min, max } = athleteRanges.find((r) => r.label === range) || {
                    min: 0,
                    max: Infinity,
                };
                return athleteCount >= min && athleteCount <= max;
            });

        const matchesFilter =
            nameMatch && rankMatch && salesMatch && subscriptionMatch && athleteMatch;

        // Ensure the returned node always has valid levels
        // Create a safe data object that definitely has levels
        const safeData = {
            ...(node.data || {}),
            ...data,
            levels: levels || { ...defaultLevels }, // Ensure levels is always included and valid
            matchesFilter,
        };

        return {
            ...node,
            data: safeData,
        };
    });
};
